import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, concatMap, first, from, map, of } from 'rxjs';
import { LoyaltySubscription } from '../../core/models/loyalty/loyalty-subscription.model';
import { AuditService } from '../../core/services/audit.service';
import { LoyaltyService } from '../../core/services/loyalty.service';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { OrderService } from '../../core/services/order.service';
import { StoreIdService } from '../../core/services/store-id.service';
import { PremiumService } from '../../loyalty/premium/premium.service';
import { IconNameType } from '../icons/icon-name-type.enum';
import { IconComponent } from '../icons/icon.component';
import { TranslocoDirective } from '@ngneat/transloco';

type SavingsValue = { savings: number; planName: string };
type SavingsParams = { subTotal: number; plans: LoyaltySubscription[] };

const NO_SAVINGS: SavingsValue = { savings: 0, planName: '' };

@Component({
  selector: 'cwo-upgrade-sub-promo',
  imports: [CommonModule, FormsModule, TranslocoDirective, IconComponent],
  templateUrl: './upgrade-sub-promo.component.html',
  styleUrls: ['./upgrade-sub-promo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeSubPromoComponent {
  private static nextId = 0;

  isInCheckout = input<boolean>(false);
  readonly promoTextId = `upgrade-sub-promo-text-${UpgradeSubPromoComponent.nextId++}`;
  private readonly loyaltyService = inject(LoyaltyService);
  private readonly orderService = inject(OrderService);
  private readonly auditService = inject(AuditService);
  private readonly premiumService = inject(PremiumService);
  private readonly storeIdService = inject(StoreIdService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  readonly IconNameType = IconNameType;
  readonly isMultiplePlans = computed(
    () => this.premiumService.subscriptions().length > 1,
  );

  private readonly isNonSubscribedUser = computed(
    () => !this.premiumService.hasActiveSubscription(),
  );

  private readonly orderSubTotal = computed(
    () => this.orderService.order()?.subTotal,
  );

  private readonly rankedPlans = computed(() =>
    [...this.premiumService.subscriptions()].sort(
      (a, b) => ((b as any).sortOrder ?? 0) - ((a as any).sortOrder ?? 0),
    ),
  );


  private readonly savingsResource = rxResource<
    SavingsValue,
    SavingsParams | undefined
  >({
    params: () => {
      const subTotal = this.orderSubTotal();
      const plans = this.rankedPlans();
      if (!this.isNonSubscribedUser() || subTotal === undefined || !plans.length) {
        return undefined;
      }
      return { subTotal, plans };
    },

    defaultValue: NO_SAVINGS,

    stream: ({ params: { plans } }) =>
      from(plans).pipe(
        concatMap((plan) =>
          this.loyaltyService
            .getUpgradedSubscriptionSavings(plan.subscriptionName)
            .pipe(
              map((data) => ({
                savings: data?.savings ?? 0,
                planName: plan.displayName || plan.subscriptionName || '',
              })),
            ),
        ),
        first((result) => result.savings > 0, NO_SAVINGS),
        catchError((err) => {
          this.auditService.createAudit(
            () => `Failed to load subscription savings: ${err}`,
          );
          return of(NO_SAVINGS);
        }),
      ),
  });

  readonly savingsPlan = computed(() =>
    this.isNonSubscribedUser() ? this.savingsResource.value().savings : 0,
  );

  readonly planName = computed(() =>
    this.isNonSubscribedUser() ? this.savingsResource.value().planName : '',
  );

  readonly premiumRoute = this.router.createUrlTree(
    this.storeIdService.getRoutePathSegments(['premium']),
  );

  openPremiumPromo(): void {
    this.modalService.openModal(ModalName.PremiumPromo);
  }
}
